// src/context/SettingsContext.js
import React, { createContext, useState, useEffect } from "react";
import { useGetSettingsQuery } from "../services/settings";

export const SettingsContext = createContext();

// DEV-only date override helper
const getNow = () => {
  // Only allow override in development

  if (process.env.NODE_ENV !== "development") {
    return new Date();
  }

  // 1️⃣ ENV override (preferred)
  const envOverride = process.env.REACT_APP_DEV_NOW;
  if (envOverride) {
    const d = new Date(envOverride);
    console.log("Using DEV NOW override from ENV:", d.toISOString());
    if (!isNaN(d)) return d;
  }

  // 2️⃣ localStorage override (manual testing)
  const lsOverride = localStorage.getItem("devNowOverride");
  if (lsOverride) {
    const d = new Date(lsOverride);
    if (!isNaN(d)) return d;
  }

  // 3️⃣ real time
  return new Date();
};

export const SettingsProvider = ({ children }) => {
  const {
    data: settingsData,
    isLoading,
    isError,
    error,
  } = useGetSettingsQuery();

  const [settings, setSettings] = useState({});
  const [isAppOpen, setIsAppOpen] = useState(false);
  const [isDASAppOpen, setDASIsAppOpen] = useState(false);
  const [isGenAppOpen, setGenIsAppOpen] = useState(false);

  useEffect(() => {
    if (settingsData && settingsData.success) {
      const { settings: fetchedSettings } = settingsData;
      const settingsObject = {};
      fetchedSettings.forEach((setting) => {
        settingsObject[setting.name] = {
          value: setting.value,
          settingDate: setting.settingDate,
          active: setting.active,
        };
      });

      setSettings(settingsObject);
      localStorage.setItem("settings", JSON.stringify(settingsObject));

      computeIsAppOpen(settingsObject);
    }

    if (isError) {
      console.error("Error fetching settings:", error);
    }
  }, [settingsData, isError, error]);

  useEffect(() => {
    const storedSettings = localStorage.getItem("settings");
    if (storedSettings) {
      const parsedSettings = JSON.parse(storedSettings);
      setSettings(parsedSettings);
      computeIsAppOpen(parsedSettings);
    }
  }, []);

  /**
   * Function to compute if applications are open
   * based on current time and settings.
   */
  const computeIsAppOpen = (settingsObject) => {
    const getUtc = (key) =>
      settingsObject[key]?.settingDate
        ? new Date(settingsObject[key].settingDate)
        : null;

    const genOpen = getUtc("GeneralOpen");
    const genClose = getUtc("GeneralClose");
    const dasOpen = getUtc("DASOpen");
    const dasClose = getUtc("DASClose");

    if (!genOpen || !genClose || !dasOpen || !dasClose) {
      setIsAppOpen(false);
      setGenIsAppOpen(false);
      setDASIsAppOpen(false);
      return;
    }

    const now = getNow();

    const genOpenNow = now >= genOpen && now <= genClose;
    const dasOpenNow = now >= dasOpen && now <= dasClose;

    setIsAppOpen(genOpenNow || dasOpenNow);
    setGenIsAppOpen(genOpenNow);
    setDASIsAppOpen(dasOpenNow);
  };

  return (
    <SettingsContext.Provider
      value={{
        settings,
        isLoading,
        isError,
        error,
        isAppOpen,
        isDASAppOpen,
        isGenAppOpen,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};
